// Angular
import { ActivatedRouteSnapshot, Resolve } from "@angular/router";
import { inject, Injectable } from "@angular/core";
import { HttpErrorResponse, HttpParams } from "@angular/common/http";

// RX JS
import { catchError, finalize, Observable, ObservableInput, of } from "rxjs";

// Services
import { NavigationService } from "#services/navigation/navigation.service";
import { LoaderService } from "#services/loader/loader.service";
import { HttpService } from "#services/http/http.service";

// Models
import { CreateRequestOptions, UpdateRequestOptions } from "#models/base-request";

// Other
import { environment } from "#src/environments/environment";

@Injectable({
    providedIn: "root",
})
export class BaseRequestService<T, G = T>
    extends HttpService
    implements Resolve<{ data: T | null }>
{
    navigationService = inject(NavigationService);
    loaderService = inject(LoaderService);

    readonly envApiUrl = environment.API_URL;
    endpoint = ``;

    create(options: CreateRequestOptions<T>): Observable<{ message: string; data: T }> {
        const { formGroup, payload, onError, pathParams, loader = "content" } = options;
        if (loader === "content") {
            this.loaderService.show();
        }

        return this.http
            .post<{
                message: string;
                data: T;
            }>(this.replaceUrlKeys(pathParams), payload ?? formGroup.value)
            .pipe(
                catchError((error) => {
                    onError?.(error);
                    return this.handleRequestValidationError(error, formGroup);
                }),
                finalize(() => {
                    if (loader === "content") {
                        this.loaderService.hide();
                    }
                })
            );
    }

    update(options: UpdateRequestOptions<T>): Observable<{ message: string; data: T }> {
        const { id, formGroup, payload, onError, pathParams, loader = "content" } = options;
        if (loader === "content") {
            this.loaderService.show();
        }

        const pathName = id ? `/${id}` : "";

        return this.http
            .put<{
                message: string;
                data: T;
            }>(`${this.replaceUrlKeys(pathParams)}${pathName}`, payload ?? formGroup.value)
            .pipe(
                catchError((error) => {
                    onError?.(error);
                    return this.handleRequestValidationError(
                        error,
                        formGroup,
                        options.showErrorMessage
                    );
                }),
                finalize(() => {
                    if (loader === "content") {
                        this.loaderService.hide();
                    }
                })
            );
    }

    delete(
        id: number,
        pathParams?: Record<string, string | number>
    ): Observable<{ message: string }> {
        return this.http
            .delete<{ message: string }>(`${this.replaceUrlKeys(pathParams)}/${id}`)
            .pipe(
                catchError((error) => {
                    return this.handleRequestError(error);
                })
            );
    }

    getOne(
        id: number | string,
        onError?: (
            err: HttpErrorResponse,
            caught: Observable<{ data: T }>
        ) => ObservableInput<{ data: T | null }>,
        pathParams?: Record<string, string | number>
    ): Observable<{ data: T | null }> {
        const errorHandler = typeof onError === "function" ? onError : () => of({ data: null });

        return this.http
            .get<{ data: T }>(`${this.replaceUrlKeys(pathParams)}/${id}`)
            .pipe(catchError(errorHandler));
    }

    getAll(
        params: HttpParams,
        pathParams?: Record<string, string | number>
    ): Observable<{ data: G[]; meta: { total: number } }> {
        return this.http
            .get<{
                data: G[];
                meta: { total: number };
            }>(this.replaceUrlKeys(pathParams), { params })
            .pipe(
                catchError((err) => {
                    this.handleRequestError(err);
                    return of({ data: [], meta: { total: 0 } });
                })
            );
    }

    resolve(route: ActivatedRouteSnapshot): Observable<{ data: T | null }> {
        return this.getOne(route.params["id"], (err: HttpErrorResponse) => {
            return this.navigationService.handleResolvePageError(err);
        });
    }

    protected replaceUrlKeys(replacements?: Record<string, string | number>): string {
        if (!replacements) {
            return this.baseApiUrl;
        }

        let url = this.baseApiUrl;
        Object.entries(replacements).forEach(([key, value]) => {
            url = url.replace(`:${key}`, encodeURIComponent(String(value)));
        });

        return url;
    }

    protected get baseApiUrl() {
        return `${this.envApiUrl}/${this.endpoint}`;
    }
}
